import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getStudents, createStudent, updateStudent, deleteStudent } from '../../api';

const empty = { username: '', password: '', full_name: '', parent_email: '' };

export default function AdminStudents() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');

  const { data: students = [], isLoading } = useQuery({
    queryKey: ['students'], queryFn: () => getStudents().then(r => r.data)
  });

  const createMut = useMutation({
    mutationFn: createStudent,
    onSuccess: () => { qc.invalidateQueries(['students']); setForm(empty); setShowForm(false); setError(''); },
    onError: (e) => setError(e.response?.data?.detail || 'Error'),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => updateStudent(id, data),
    onSuccess: () => { qc.invalidateQueries(['students']); setEditing(null); setForm(empty); setError(''); },
    onError: (e) => setError(e.response?.data?.detail || 'Error'),
  });

  const deleteMut = useMutation({
    mutationFn: deleteStudent,
    onSuccess: () => qc.invalidateQueries(['students']),
  });

  const handleSubmit = (e) => {
    e.preventDefault(); setError('');
    if (editing) {
      const payload = { full_name: form.full_name, parent_email: form.parent_email };
      if (form.password) payload.password = form.password;
      updateMut.mutate({ id: editing, data: payload });
    } else {
      createMut.mutate(form);
    }
  };

  const startEdit = (s) => {
    setEditing(s.id); setShowForm(true);
    setForm({ username: s.username, password: '', full_name: s.full_name || '', parent_email: s.parent_email || '' });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 26 }}>Students</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Manage student accounts</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowForm(true); setEditing(null); setForm(empty); }}>
          + Add Student
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 20 }}>{editing ? 'Edit Student' : 'Add New Student'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">Username *</label>
                <input className="form-input" value={form.username} disabled={!!editing}
                  onChange={e => setForm(f => ({ ...f, username: e.target.value }))} required={!editing} />
              </div>
              <div className="form-group">
                <label className="form-label">{editing ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                <input className="form-input" type="password" value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required={!editing} />
              </div>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="form-input" value={form.full_name}
                  onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Parent Email</label>
                <input className="form-input" type="email" value={form.parent_email}
                  onChange={e => setForm(f => ({ ...f, parent_email: e.target.value }))} />
              </div>
            </div>
            {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" type="submit">{editing ? 'Update' : 'Create Student'}</button>
              <button className="btn btn-ghost" type="button"
                onClick={() => { setShowForm(false); setEditing(null); setError(''); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        {isLoading ? <div className="spinner"></div> : (
          <table className="table">
            <thead><tr>
              <th>Name</th><th>Username</th><th>Parent Email</th><th>Status</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {students.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                  No students yet.
                </td></tr>
              ) : students.map(s => (
                <tr key={s.id}>
                  <td style={{ fontWeight: 500 }}>{s.full_name || '—'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{s.username}</td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{s.parent_email || '—'}</td>
                  <td><span className={`badge ${s.is_active ? 'badge-green' : 'badge-red'}`}>
                    {s.is_active ? 'Active' : 'Inactive'}
                  </span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-ghost btn-sm"
                        onClick={() => navigate(`/admin/students/${s.id}/tests`)}>
                        Tests
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => startEdit(s)}>Edit</button>
                      <button className="btn btn-danger btn-sm"
                        onClick={() => window.confirm('Delete?') && deleteMut.mutate(s.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
